import { useNavigate } from "@solidjs/router";
import { createSignal, Show } from "solid-js";
import { CalendarIcon } from "@/shared/components/icons";
import { signIn, token } from "./session";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  if (token()) navigate("/", { replace: true });

  // Not routed through the generated client: /auth/login is the one endpoint
  // that exists to obtain a token, so it has no Authorization header to add and
  // no 401 handling to apply.
  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email(), password: password() }),
      });
      const body = (await response.json()) as {
        token?: string;
        error?: string;
      };
      if (!response.ok || !body.token) {
        setError(body.error ?? "Login failed");
        return;
      }
      signIn(body.token);
      // Not to a fixed page: which one this user may see depends on their
      // permissions, and those arrive with the next request.
      navigate("/", { replace: true });
    } catch {
      setError("Connection error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="login-wrapper">
      <div class="login-card">
        <div
          class="logo"
          style={{ "justify-content": "center", "margin-bottom": "24px" }}
        >
          <CalendarIcon size={28} />
          <span>Business Manager</span>
        </div>
        <form onSubmit={onSubmit}>
          <div class="form-group">
            <label for="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              required
              autofocus
              autocomplete="username"
              placeholder="you@example.com"
              value={email()}
              onInput={(event) => setEmail(event.currentTarget.value)}
            />
          </div>
          <div class="form-group">
            <label for="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              required
              autocomplete="current-password"
              placeholder="Enter password"
              value={password()}
              onInput={(event) => setPassword(event.currentTarget.value)}
            />
          </div>
          <Show when={error()}>
            <div
              style={{
                color: "var(--accent-red)",
                "font-size": "13px",
                "text-align": "center",
                "margin-bottom": "12px",
              }}
            >
              {error()}
            </div>
          </Show>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy()}
            style={{
              width: "100%",
              "justify-content": "center",
              padding: "10px",
            }}
          >
            {busy() ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p
          style={{
            color: "var(--text-muted)",
            "font-size": "12px",
            "text-align": "center",
            "margin-top": "16px",
          }}
        >
          Forgotten your password? Ask an administrator for a reset link.
        </p>
      </div>
    </div>
  );
}
